import { Box, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import clockWise from "../../assets/imgs/crime/clockwise.png";
import { menLinksSmall, womenLinksSmall } from "../../constants/avatarLinks";
import {
  City,
  CitySimple,
  GAME_CASH_TRANSFER_COOLDOWN,
} from "../../constants/const";
import { goToMapSlotPage } from "../../helper/map";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  claimBankProfitAction,
  getBusinessProfileAction,
  getLastTransferTimeAction,
  transferGameCashAction,
} from "../../reducers/game.slice";
import {
  convertDateTimeSimple,
  shortAddress,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";

import useStyles from "./index.styles";

const Bank = () => {
  const { classes } = useStyles();
  const { cityId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { myProfile } = useAppSelector((state) => state.profile);
  const currentTime = useCurrentTime();

  const { account } = useWallet();
  const { balance, updateBalance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );
  const { lastTransferTime, businessProfile } = useAppSelector(
    (state) => state.game
  );
  const [amount, setAmount] = useState(0);
  const [recipientAddress, setRecipientAddress] = useState("");
  const { profileNames } = useAppSelector((state) => state.profile);
  const [remainingTime, setRemainingTime] = useState(0);

  const isMyBusiness =
    account?.toLowerCase() === businessProfile?.address?.toLowerCase();

  const handleTransfer = () => {
    const recipientAddr =
      profileNames.find(
        (profile) =>
          profile.name?.toLowerCase() === recipientAddress.toLowerCase()
      )?.address || "";

    dispatch(
      transferGameCashAction({
        account,
        amount,
        recipientAddress: recipientAddr,
        signMsg,
        signature,
      })
    )
      .unwrap()
      .then(() => {
        toastSuccess("Transfer successfully");
        setAmount(0);
        setRecipientAddress("");
        updateBalance(account, signMsg, signature);
        dispatch(getLastTransferTimeAction(account));
        dispatch(
          getBusinessProfileAction({ itemId: 42, cityId: myProfile.city || 0 })
        );
      })
      .catch((error) => {
        // toastError(error);
      });
  };

  const handleClaimProfit = () => {
    dispatch(claimBankProfitAction({ account, cityId: myProfile.city || 0 }))
      .unwrap()
      .then(() => {
        toastSuccess("Claim profit successfully");
      })
      .catch((error) => {
        // toastError(error);
      });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime() / 1000;
      if (!lastTransferTime) {
        setRemainingTime(0);
        return;
      }
      const lastTime = lastTransferTime + GAME_CASH_TRANSFER_COOLDOWN;
      const diff = lastTime - now;
      setRemainingTime(Math.max(diff, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [lastTransferTime]);

  useEffect(() => {
    dispatch(getLastTransferTimeAction(account));
  }, [account, dispatch]);

  useEffect(() => {
    dispatch(
      getBusinessProfileAction({ itemId: 42, cityId: myProfile.city || 0 })
    );
  }, [myProfile.city, dispatch]);

  const isEnableTransfer = useMemo(() => {
    return (
      amount > 0 &&
      amount <= balance &&
      recipientAddress &&
      profileNames.some(
        (profile) =>
          profile.name?.toLowerCase() === recipientAddress.toLowerCase()
      ) &&
      remainingTime === 0
    );
  }, [amount, recipientAddress, balance, profileNames, remainingTime]);

  const profileAddress = useMemo(() => {
    return profileNames.find(
      (profile) => profile.name?.toLowerCase() === recipientAddress.toLowerCase()
    )?.address
  }, [recipientAddress, profileNames]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.title}>
              <Typography
                fontFamily={"Philosopher"}
                fontSize={36}
                fontWeight={700}
                color={"#FFFFFF"}
              >
                The bank
              </Typography>
            </Box>
          </Box>
          <Box className={classes.headerRight}>{City[Number(cityId)]}</Box>
        </Box>

        <Box className={classes.content}>
          <Box className={classes.contentLeft}>
            <Box className={classes.balanceTitle}>
              <Box>Balance</Box>
              <Box
                className={classes.viewHistory}
                onClick={() => navigate("/cash/history")}
              >
                View history
              </Box>
            </Box>

            <Box className={classes.balanceBox}>
              <Box component={"img"} src={"/assets/imgs/layout/currency.png"} />
              <Box>$ {toUSDFormat(balance, 0)}</Box>
            </Box>

            <Box className={classes.transferTitle}>
              <Box>Transfer</Box>
              <Box className={classes.transferTitleRight}>
                <Box>10% fee:</Box>
                <Box>
                  <Box
                    component={"img"}
                    src={"/assets/imgs/layout/currency.png"}
                  />
                  <Box> {toUSDFormat(amount * 0.1, 0)}</Box>
                </Box>
              </Box>
            </Box>

            <Box className={classes.transferInputBox}>
              <Box className={classes.transferInputBoxLeft}>
                <Box className={classes.transferInputBoxLeftTop}>
                  <Box
                    component={"img"}
                    src={"/assets/imgs/layout/currency.png"}
                  />

                  <input
                    type="number"
                    className={classes.priceInput}
                    placeholder="0"
                    min={0}
                    max={balance}
                    step={1}
                    value={Number(amount).toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sanitizedValue = value.toString().replace(/^0+(?=\d)/, ""); // Remove leading zeros
                      setAmount(Number(sanitizedValue));
                    }}
                  />

                  <Button
                    className={classes.allButton}
                    onClick={() => setAmount(balance)}
                  >
                    All
                  </Button>
                </Box>
                <Box className={classes.greyColor}>Amount</Box>
              </Box>
              <Box className={classes.transferInputBoxRight}>
                <Box className={classes.transferInputBoxRightTop}>
                  <input
                    className={classes.priceInput}
                    placeholder=""
                    onChange={(e) => setRecipientAddress(e.target.value)}
                    min={0}
                    step={1}
                    style={{ textAlign: "center" }}
                    value={recipientAddress}
                  />
                </Box>
                <Box className={classes.greyColor}>{profileAddress ? shortAddress(profileAddress) : "Recipient"}</Box>
              </Box>
            </Box>

            <Box className={classes.transferButtonBox}>
              <Button
                className={classes.transferButton}
                onClick={handleTransfer}
                disabled={!isEnableTransfer}
              >
                Confirm Transfer
              </Button>
            </Box>
          </Box>

          <Box className={classes.contentRight}>
            <Box className={classes.contentRightTop}>
              <Box
                component={"img"}
                src={clockWise}
                className={classes.clockWise}
              />
              <Box className={classes.remainMin}>
                {remainingTime > 0
                  ? `${Math.floor(remainingTime / 60)} minutes`
                  : "Now"}
              </Box>
              <Box className={classes.remainSec}>
                {remainingTime > 0
                  ? `${Math.floor(remainingTime % 60)} seconds`
                  : ""}
              </Box>
              <Box className={classes.nextCashTransfer}>Next cash transfer</Box>
            </Box>
            <Box className={classes.contentRightBottom}>
              <Box className={classes.contentRightBottomLeft}>
                {businessProfile?.ownerImageId ? (
                  <Box
                    component="img"
                    className={classes.profileImg}
                    src={
                      businessProfile?.ownerGender === 1
                        ? womenLinksSmall[
                            !businessProfile.ownerImageId
                              ? -1
                              : businessProfile.ownerImageId
                          ]
                        : menLinksSmall[
                            !businessProfile?.ownerImageId
                              ? -1
                              : businessProfile.ownerImageId
                          ]
                    }
                  ></Box>
                ) : (
                  <Box sx={{ minWidth: 100 }}></Box>
                )}
              </Box>
              <Box className={classes.contentRightBottomRight}>
                <Box>
                  <Typography>Owner:</Typography>
                  <Typography
                    sx={{ cursor: "pointer", color: "#1c7995" }}
                    onClick={() =>
                      navigate(`/profile/${businessProfile?.ownerName}`)
                    }
                  >
                    {businessProfile?.ownerName}
                  </Typography>
                </Box>
                <Box>
                  <Typography>Family:</Typography>
                  <Typography>{businessProfile?.familyInfo?.name}</Typography>
                </Box>
                <Box>
                  <Typography>Location:</Typography>
                  <Typography
                    sx={{ color: "#1c7995", cursor: "pointer" }}
                    onClick={() =>
                      businessProfile &&
                      businessProfile.map &&
                      goToMapSlotPage(
                        businessProfile.cityId,
                        businessProfile.map?.slotX,
                        businessProfile.map?.slotY
                      )
                    }
                  >
                    {businessProfile &&
                      businessProfile?.cityId >= 0 &&
                      `#${CitySimple[businessProfile.cityId]}-${
                        businessProfile.map?.slotX
                      }-${businessProfile.map?.slotY}`}
                  </Typography>
                </Box>
                <Box>
                  <Typography>Profit Cash:</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      component={"img"}
                      src={"/assets/imgs/layout/currency.png"}
                      width={24}
                    />
                    <Box>{toUSDFormat(businessProfile?.profit, 0)}</Box>
                  </Box>
                </Box>
                <Box>
                  <Typography>Time owned:</Typography>
                  <Typography>
                    {convertDateTimeSimple(
                      currentTime -
                        (businessProfile?.timestamp || currentTime - 10000000)
                    )}
                  </Typography>
                </Box>

                {isMyBusiness && (
                  <Button
                    className={classes.claimButton}
                    onClick={handleClaimProfit}
                  >
                    Claim profit
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Bank;
